import Blue from '~/core';
import prism from '~/vendor/_prism';

new Blue({
  name: 'Style Guide Background Generator',
  selector: '#CompositionBackgrounds',
  setup(dropPoint) {
    const styles = [...self.Root.stylesheet.cssRules[0].style]
      .filter(s => s.startsWith('--fill-'))
      .sort()
      .reverse();

    styles.forEach(style => {
      dropPoint.insertAdjacentHTML(
        'afterend',
        `
<blue-composition class="composition${style} composition--padded">
  <p class="composition-fill-info"></p>
  <pre><code class="language-html"> &lt;blue-composition class=&quot;composition${style}&quot;&gt; &lt;/blue-composition&gt;</code></pre>
</blue-composition>`
      );
    });
  },
});

new Blue({
  name: 'Style Guide Composition Builder',
  selector: '.composition-builder',
  setup(builder) {
    const padding = builder.querySelector('.padding select');
    const paddingMod = builder.querySelector('.padding-modifiers select');
    const paddingModContainer = builder.querySelector('.padding-modifiers');
    const margin = builder.querySelector('.margin select');
    const marginMod = builder.querySelector('.margin-modifiers select');
    const marginModContainer = builder.querySelector('.margin-modifiers');

    const animateControl = (el, start, end) => {
      el.animate(
        [
          {
            height: `${start.height}px`,
            marginBottom: `${start.margin}`,
          },
          {
            height: `${end.height}px`,
            marginBottom: `${end.margin}`,
          },
        ],
        {
          duration: 250,
          easing: 'ease',
        }
      );
    };

    padding.addEventListener('input', () => {
      let start = {},
        end = {};
      start.height = paddingModContainer.offsetHeight;
      start.margin = getComputedStyle(paddingModContainer).marginBottom;
      builder.classList.remove('composition--padded', 'composition--padded-sm');
      if (padding.value) {
        builder.classList.add(
          padding.value === 'std'
            ? 'composition--padded'
            : 'composition--padded-sm'
        );
        paddingModContainer.classList.add('active');
      } else {
        paddingModContainer.classList.remove('active');
      }
      end.height = paddingModContainer.offsetHeight;
      end.margin = getComputedStyle(paddingModContainer).marginBottom;
      animateControl(paddingModContainer, start, end);
    });

    paddingMod.addEventListener('input', () => {
      builder.classList.remove(
        'composition--padded-flush-top',
        'composition--padded-flush-bottom'
      );
      if (padding.value) {
        if (paddingMod.value === 'top') {
          builder.classList.add('composition--padded-flush-top');
        } else if (paddingMod.value === 'bottom') {
          builder.classList.add('composition--padded-flush-bottom');
        }
      }
    });

    margin.addEventListener('input', () => {
      let start = {},
        end = {};
      start.height = marginModContainer.offsetHeight;
      start.margin = getComputedStyle(marginModContainer).marginBottom;
      builder.classList.remove('composition--spaced', 'composition--spaced-sm');
      if (margin.value) {
        builder.classList.add(
          margin.value === 'std'
            ? 'composition--spaced'
            : 'composition--spaced-sm'
        );
        marginModContainer.classList.add('active');
      } else {
        marginModContainer.classList.remove('active');
      }
      end.height = marginModContainer.offsetHeight;
      end.margin = getComputedStyle(marginModContainer).marginBottom;
      animateControl(marginModContainer, start, end);
    });

    marginMod.addEventListener('input', () => {
      builder.classList.remove(
        'composition--spaced-flush-top',
        'composition--spaced-flush-bottom'
      );
      if (margin.value) {
        if (marginMod.value === 'top') {
          builder.classList.add('composition--spaced-flush-top');
        } else if (marginMod.value === 'bottom') {
          builder.classList.add('composition--spaced-flush-bottom');
        }
      }
    });
  },
});

const whitespace_config = {
  'remove-trailing': true,
  'remove-indent': true,
  'left-trim': true,
  'right-trim': true,
  'spaces-to-tabs': 2,
};
prism();
Prism.plugins.NormalizeWhitespace.setDefaults(whitespace_config);

new Blue({
  name: 'Style Guide Icons',
  selector: '.styleguide__icons',
  async setup(ul) {
    const response = await fetch('/fonts/icons/selection.json');
    if (!response.ok) return;
    let icons = await response.json();
    let preferences = icons.preferences;
    icons = icons.icons;
    icons.forEach(icon => {
      const cls = `${preferences.fontPref.prefix}${icon.properties.name}`;
      const content = `${getCSSHex(icon.properties.code)}`;

      const li = this.$createElement({
        tag: 'li',
        attributes: { class: 'styleguide__icon' },
      });
      const item = this.$createElement({
        tag: 'div',
        attributes: { class: 'styleguide__icon__item' },
      });
      const i = this.$createElement({
        tag: 'i',
        attributes: { class: cls },
      });
      const htmlPre = this.$createElement({
        tag: 'pre',
        attributes: { class: `language-html}` },
      });
      const htmlCode = this.$createElement({
        tag: 'code',
        attributes: { class: `language-html}` },
        text: `<i class="${cls}"></i>`,
      });
      const cssPre = this.$createElement({
        tag: 'pre',
        attributes: { class: `language-css}` },
      });
      const cssCode = this.$createElement({
        tag: 'code',
        attributes: { class: `language-css}` },
        text: `&::before {
  @include icon;
  content: "\\${content}";
}`,
      });
      htmlPre.insertAdjacentElement('beforeend', htmlCode);
      item.insertAdjacentElement('beforeend', htmlPre);
      cssPre.insertAdjacentElement('beforeend', cssCode);
      item.insertAdjacentElement('beforeend', cssPre);
      item.insertAdjacentElement('afterbegin', i);
      li.insertAdjacentElement('beforeend', item);
      ul.insertAdjacentElement('beforeend', li);
    });
    prism();

    function getCSSHex(code) {
      let decimalValue = String(code).replace(/\D/g, '');
      let hexValue = Number(decimalValue).toString(16);
      while (hexValue.length < 4) {
        hexValue = '0' + hexValue;
      }
      return hexValue;
    }
  },
});

new Blue({
  name: 'Style Guide Component Markup',
  selector: '.styleguide__markup',
  setup(wrapper) {
    const trigger = wrapper.querySelector(
      '.styleguide__markup__name, .styleguide__markup__toggle'
    );
    const content = wrapper.querySelector('.styleguide__markup__code');
    const container = wrapper.querySelector('.styleguide__markup__code code');
    let processed_code = process(
      container.textContent.replace(/(\t|\n)/gm, '').replace(/blue/gm, 'temp')
    );

    processed_code = Prism.plugins.NormalizeWhitespace.normalize(
      processed_code,
      whitespace_config
    );
    processed_code = Prism.highlight(
      processed_code,
      Prism.languages.html,
      'html'
    );

    processed_code = processed_code.replace(/temp/gm, 'blue');

    container.innerHTML = processed_code;

    new this.$toggle({
      trigger,
      content,
    });
  },
});

new Blue({
  name: 'Style Guide Markup Nav',
  selector: '.styleguide__nav a',
  setup(link) {
    let linkText = link.textContent;
    let baseURL = window.location.origin + window.location.pathname;
    let dest = link.getAttribute('href');
    this.$addEvents(link, 'click.stop.prevent enter.stop.prevent', () => {
      history.pushState({}, linkText, baseURL + dest);
      window.scrollTo({
        top: document.querySelector(dest).getBoundingClientRect().top,
        behavior: 'smooth',
      });
    });
  },
});

new Blue({
  name: 'Style Guide Grid Builder',
  selector: '.styleguide__grid-builder',
  setup(builder) {
    const add = builder.querySelector('.add');
    const remove = builder.querySelector('.remove');
    const even = builder.querySelector('.even');
    const width = builder.querySelector('.width');
    const media = builder.querySelector('.media');
    const guttersDef = builder.querySelector('.gutters--def');
    const guttersSm = builder.querySelector('.gutters--sm');
    const guttersLg = builder.querySelector('.gutters--lg');
    const centered = builder.querySelector('.centered');

    const grid = builder.querySelector('.styleguide__grid-builder__grid .g');
    const code = builder.querySelector('.styleguide__grid-builder__code code');

    let is_even = even.checked;
    let columns = 4;

    const input_settings = () => {
      return {
        class: 'width',
        type: 'number',
        step: 1,
        min: 1,
        max: 12,
        value: width.value,
      };
    };

    const column_settings = (overwrite = false) => {
      return {
        class: `g__c g__c--${media.value}${
          overwrite ? overwrite : width.value
        }`,
      };
    };

    const make_column = () => {
      let input = this.$createElement({
        tag: 'input',
        attributes: input_settings(),
      });
      let col = this.$createElement({
        tag: 'div',
        attributes: column_settings(),
        html: input,
      });

      input.addEventListener('input', () => do_input.apply(input, [true]));

      return col;
    };

    const make_html = () => {
      const grid_code = grid.cloneNode(true);
      let processed_code;
      grid_code.querySelectorAll('.g__c').forEach(col => {
        col.innerHTML = '';
        while (col.attributes.length > 1) {
          if (col.attributes[0].name != 'class') {
            col.removeAttribute(col.attributes[0].name);
          } else {
            col.removeAttribute(col.attributes[1].name);
          }
        }
      });

      processed_code = process(grid_code.outerHTML);

      processed_code = Prism.plugins.NormalizeWhitespace.normalize(
        processed_code,
        whitespace_config
      );

      processed_code = Prism.highlight(
        processed_code,
        Prism.languages.html,
        'html'
      );

      code.innerHTML = processed_code;
    };

    const do_reset = () => {
      code.innerText = '';
      grid.innerHTML = '';
      for (let i = 0; i < columns; i++) {
        grid.insertAdjacentElement('beforeend', make_column());
      }
      make_html();
    };

    const do_add = () => {
      columns++;
      grid.insertAdjacentElement('beforeend', make_column());
      make_html();
    };

    const do_remove = () => {
      columns--;
      grid.removeChild(grid.lastElementChild);
      make_html();
    };

    const do_input = function (col = false) {
      if (is_even) {
        builder
          .querySelectorAll('.width')
          .forEach(width => (width.value = this.value));
        do_reset();
      } else if (col) {
        let col = this.closest('.g__c');
        col.setAttribute('class', column_settings(this.value).class);
        make_html();
      }
    };

    const do_media = function () {
      if (is_even) {
        do_reset();
      } else {
        grid.querySelectorAll('.g__c').forEach(col => {
          col.setAttribute(
            'class',
            column_settings(col.querySelector('input').value).class
          );
        });
        make_html();
      }
    };

    const do_gutters = (size = null) => {
      grid.classList.remove('g--gutter-sm', 'g--gutter-lg');
      if (size) {
        grid.classList.add(`g--gutter${size}`);
      }
      make_html();
    };

    const do_centered = function () {
      let columns = grid.querySelectorAll('.g__c');
      columns.forEach(c => {
        c._start = c.getBoundingClientRect().left;
      });

      if (this.checked) grid.classList.add('g--centered');
      else grid.classList.remove('g--centered');

      columns.forEach(c => {
        c._end = c.getBoundingClientRect().left;
      });

      columns.forEach(c =>
        c.animate(
          [
            { transform: `translateX(${c._start - c._end}px)` },
            { transform: 'translateX(0)' },
          ],
          {
            duration: 250,
            easing: 'ease',
          }
        )
      );
      make_html();
    };

    add.addEventListener('click', do_add);
    remove.addEventListener('click', do_remove);
    width.addEventListener('input', () => do_input.apply(width, [false]));
    media.addEventListener('input', do_media);

    even.addEventListener('input', () =>
      even.checked ? ((is_even = true), do_reset()) : (is_even = false)
    );
    guttersDef.addEventListener('input', () => do_gutters());
    guttersSm.addEventListener('input', () => do_gutters('-sm'));
    guttersLg.addEventListener('input', () => do_gutters('-lg'));
    centered.addEventListener('input', do_centered);

    do_reset();
  },
});

function process(str) {
  var div = document.createElement('div');
  div.innerHTML = str.trim();

  return format(div, 0).innerHTML;
}

function format(node, level) {
  var indentBefore = new Array(level++ + 1).join('  '),
    indentAfter = new Array(level - 1).join('  '),
    textNode;

  for (var i = 0; i < node.children.length; i++) {
    textNode = document.createTextNode('\n' + indentBefore);
    node.insertBefore(textNode, node.children[i]);

    format(node.children[i], level);

    if (node.lastElementChild == node.children[i]) {
      textNode = document.createTextNode('\n' + indentAfter);
      node.appendChild(textNode);
    }
  }

  return node;
}
